import axios from 'axios';
import type {
  OnboardingAnalysisResult,
  BannerResult,
  PlanningAnalysisResponse,
  BannerBatchParams,
  BannerBatchResponse,
  BannerBatchStatusResponse,
} from '../../types/onboarding';

// Onboarding API does not require authentication
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const baseURL = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const onboardingApi = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000,
});

// Response types
interface AnalyzeUrlResponse {
  success: boolean;
  data: OnboardingAnalysisResult;
  lead_id: number;
  message?: string;
}

interface GenerateBannersResponse {
  success: boolean;
  session_id: string;
  banners: BannerResult[];
  progress_percent: number;
}

interface BannerStatusResponse {
  success: boolean;
  session_id: string;
  status: 'processing' | 'completed' | 'failed';
  banners: BannerResult[];
  progress_percent: number;
  completed_count: number;
  total_count: number;
}

/**
 * POST /api/onboarding/analyze-url
 */
export async function analyzeUrl(url: string): Promise<{
  result: OnboardingAnalysisResult;
  leadId: number;
}> {
  const response = await onboardingApi.post<AnalyzeUrlResponse>('/onboarding/analyze-url', { url });
  const data = response.data;

  if (!data.success) {
    throw new Error(data.message || 'URL analysis failed');
  }

  return {
    result: data.data,
    leadId: data.lead_id,
  };
}

/**
 * POST /api/onboarding/generate-banners
 */
export async function generateBanners(params: {
  title: string;
  description: string | null;
  og_image_url: string | null;
  colors: string[];
  domain: string | null;
}): Promise<{
  sessionId: string;
  banners: BannerResult[];
}> {
  const response = await onboardingApi.post<GenerateBannersResponse>('/onboarding/generate-banners', params);
  const data = response.data;

  if (!data.success) {
    throw new Error('Banner generation failed');
  }

  return {
    sessionId: data.session_id,
    banners: data.banners,
  };
}

/**
 * GET /api/onboarding/banner-status/{sessionId}
 */
export async function getBannerStatus(sessionId: string): Promise<BannerStatusResponse> {
  const response = await onboardingApi.get<BannerStatusResponse>(`/onboarding/banner-status/${sessionId}`);
  return response.data;
}

/**
 * POST /api/onboarding/collect-email
 */
export async function collectEmail(params: {
  email: string;
  lead_id: number | null;
}): Promise<void> {
  await onboardingApi.post('/onboarding/collect-email', params);
}

/**
 * POST /api/onboarding/create-checkout (authenticated user)
 */
export async function createCheckout(): Promise<{
  checkoutUrl: string;
  sessionId: string;
}> {
  const response = await onboardingApi.post<{
    success: boolean;
    checkout_url: string;
    session_id: string;
    message?: string;
  }>('/onboarding/create-checkout');

  if (!response.data.success) {
    throw new Error(response.data.message || 'Checkout creation failed');
  }

  return {
    checkoutUrl: response.data.checkout_url,
    sessionId: response.data.session_id,
  };
}

/**
 * POST /api/onboarding/create-checkout-guest
 */
export async function createCheckoutGuest(params: {
  email: string | null;
  lead_id: number | null;
  price_id?: string;
}): Promise<{
  checkoutUrl: string;
  sessionId: string;
}> {
  const response = await onboardingApi.post<{
    success: boolean;
    checkout_url: string;
    session_id: string;
    message?: string;
  }>('/onboarding/create-checkout-guest', params);

  if (!response.data.success) {
    throw new Error(response.data.message || 'Checkout creation failed');
  }

  return {
    checkoutUrl: response.data.checkout_url,
    sessionId: response.data.session_id,
  };
}

/**
 * POST /api/onboarding/create-subscription
 * Create a subscription with inline Stripe Elements (no redirect)
 */
export async function createSubscription(params: {
  payment_method_id: string;
  email: string | null;
  lead_id: number | null;
}): Promise<{ success: boolean; subscription_id: string }> {
  const response = await onboardingApi.post<{
    success: boolean;
    subscription_id: string;
    message?: string;
  }>('/onboarding/create-subscription', params);

  if (!response.data.success) {
    throw new Error(response.data.message || 'Subscription creation failed');
  }

  return response.data;
}

/**
 * POST /api/onboarding/analyze-planning
 * Analyze URL for planning data (product, competitor, brand, key copies)
 */
export async function analyzePlanning(url: string): Promise<PlanningAnalysisResponse> {
  const response = await onboardingApi.post<{
    success: boolean;
    data: PlanningAnalysisResponse;
    message?: string;
  }>('/onboarding/analyze-planning', { url });

  if (!response.data.success) {
    throw new Error(response.data.message || 'Planning analysis failed');
  }

  return response.data.data;
}

/**
 * POST /api/onboarding/generate-banners-batch
 * Generate banners in batch using planning data
 */
export async function generateBannersBatch(params: BannerBatchParams): Promise<BannerBatchResponse> {
  const response = await onboardingApi.post<BannerBatchResponse>('/onboarding/generate-banners-batch', params);

  if (!response.data.success) {
    throw new Error('Banner batch generation failed');
  }

  return response.data;
}

/**
 * GET /api/onboarding/banner-batch-status/{sessionId}
 * Check status of batch banner generation
 */
export async function getBannerBatchStatus(sessionId: string): Promise<BannerBatchStatusResponse> {
  const response = await onboardingApi.get<BannerBatchStatusResponse>(`/onboarding/banner-batch-status/${sessionId}`);
  return response.data;
}

export default {
  analyzeUrl,
  generateBanners,
  getBannerStatus,
  collectEmail,
  createCheckoutGuest,
  createSubscription,
  analyzePlanning,
  generateBannersBatch,
  getBannerBatchStatus,
};
